import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import { NoPackagingDecisionRow } from '@/shared/components/packaging/NoPackagingDecisionRow'
import { useUnlinkPackagingItemFromPackedItem } from '@/shared/hooks/api/packagingSuppliers'
import {
  useGetPackedItem,
  useMarkPackedItemHasNoPackaging,
} from '@/shared/hooks/api/packedItemsOverview'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { ThemeSwitch } from '@/components/theme-switch'
import { PackagedItemRedFlagsCell } from './PackagedItemRedFlagsCell'
import { PackagingComplianceProgressBadge } from './PackagingComplianceProgressBadge'
import { PackedItemSkuCell } from './PackedItemSkuCell'
import { PackagingItemAuditCell } from './PackagingItemAuditCell'
import { buildPackagingItemDetailFromPackedItemSearch } from './packagingItemDetailNavigation'

export function PackedItemDetailsPage() {
  const { packedItemId = '' } = useParams<{ packedItemId: string }>()
  const { t } = useTranslation(['packagingSuppliers', 'common'])
  const canWriteOrgData = useCanWriteOrgData()
  const { data: packedItem, isLoading, isError } = useGetPackedItem(packedItemId)
  const unlinkPackagingItem = useUnlinkPackagingItemFromPackedItem()
  const markHasNoPackaging = useMarkPackedItemHasNoPackaging(packedItemId)

  const shell = (content: ReactNode) => (
    <>
      <Header fixed>
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>
      <Main className='flex flex-1 flex-col gap-6 px-6'>{content}</Main>
    </>
  )

  if (isLoading) {
    return shell(
      <div className='py-12 text-center text-muted-foreground'>
        {t('packedItemDetail.loading')}
      </div>
    )
  }

  if (isError || !packedItem) {
    return shell(
      <div className='space-y-3'>
        <h2 className='text-2xl font-bold tracking-tight'>
          {t('packedItemDetail.notFound')}
        </h2>
        <Link
          to='/packaging/packed-items'
          className='text-sm text-primary underline'
        >
          {t('tabs.packedItems')}
        </Link>
      </div>
    )
  }

  return shell(
    <>
      <div className='space-y-2'>
        <h2 className='text-2xl font-bold tracking-tight'>{packedItem.name}</h2>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to='/packaging/packed-items'>{t('tabs.packedItems')}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className='max-w-[min(100%,28rem)] truncate'>
                {packedItem.name}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <div className='grid gap-2 sm:grid-cols-2 lg:grid-cols-3'>
        <Item variant='outline'>
          <ItemContent>
            <ItemTitle>{t('packedItems.columnSku')}</ItemTitle>
            <ItemDescription>
              <PackedItemSkuCell
                sku={packedItem.sku}
                notInSourceList={packedItem.notInSourceList}
                fromPartsCatalog={packedItem.fromPartsCatalog}
              />
            </ItemDescription>
          </ItemContent>
        </Item>
        <Item variant='outline'>
          <ItemContent>
            <ItemTitle>{t('list.columnComplianceProgress')}</ItemTitle>
            <ItemDescription>
              <PackagingComplianceProgressBadge
                completed={
                  packedItem.packagingItems.filter(
                    (packagingItem) => packagingItem.completedAt != null
                  ).length
                }
                total={packedItem.packagingItems.length}
                allCompleted={
                  packedItem.complianceProgress === 'ALL_ITEMS_COMPLETE'
                }
              />
            </ItemDescription>
          </ItemContent>
        </Item>
        <Item variant='outline'>
          <ItemContent>
            <ItemTitle>{t('list.columnAudit')}</ItemTitle>
            <ItemDescription>
              <PackagedItemRedFlagsCell
                redFlags={packedItem.redFlags}
                redFlagDetails={packedItem.redFlagDetails}
                packagingItems={packedItem.packagingItems}
              />
            </ItemDescription>
          </ItemContent>
        </Item>
      </div>

      <div className='grid gap-2 sm:grid-cols-2'>
        <Item variant='outline'>
          <ItemContent>
            <ItemTitle>{t('packedItemDetail.description')}</ItemTitle>
            <ItemDescription className='whitespace-pre-wrap'>
              {packedItem.description}
            </ItemDescription>
          </ItemContent>
        </Item>
        <Item
          variant='outline'
          render={
            <Link to={`/packaging/suppliers/${packedItem.supplier.id}`} />
          }
        >
          <ItemContent>
            <ItemTitle>{t('packedItems.columnSupplier')}</ItemTitle>
            <ItemDescription className='underline-offset-4 group-hover/item:underline'>
              {packedItem.supplier.name}
            </ItemDescription>
          </ItemContent>
        </Item>
      </div>

      <section className='space-y-3'>
        <h3 className='text-sm font-semibold tracking-tight'>
          {t('packedItemDetail.packagingItems')}
        </h3>
        {packedItem.packagingItems.length === 0 ? (
          <div className='space-y-3'>
            <p className='text-sm text-muted-foreground'>
              {t('packedItems.emptyPackagingItems')}
            </p>
            <NoPackagingDecisionRow
              checked={packedItem.hasNoPackaging}
              disabled={!canWriteOrgData || markHasNoPackaging.isPending}
              label={t('packedItems.noPackaging.label')}
              onCheckedChange={(hasNoPackaging) => {
                void markHasNoPackaging.mutateAsync(hasNoPackaging)
              }}
            />
          </div>
        ) : (
          <div className='overflow-hidden rounded-md border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('list.columnName')}</TableHead>
                  <TableHead>{t('list.columnComplianceProgress')}</TableHead>
                  <TableHead>{t('list.columnAudit')}</TableHead>
                  {canWriteOrgData && (
                    <TableHead className='text-end'>
                      {t('list.columnActions')}
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {packedItem.packagingItems.map((packagingItem) => (
                  <TableRow key={packagingItem.id}>
                    <TableCell className='font-medium'>
                      <Link
                        to={`/packaging/suppliers/${packedItem.supplier.id}/items/${packagingItem.id}${buildPackagingItemDetailFromPackedItemSearch(packedItem.id)}`}
                        className='text-foreground underline decoration-dotted underline-offset-2'
                      >
                        {packagingItem.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {packagingItem.completedAt == null
                        ? t('packedItems.packagingItemDraft')
                        : t('packedItems.packagingItemComplete')}
                    </TableCell>
                    <TableCell>
                      <PackagingItemAuditCell
                        auditStatus={packagingItem.auditStatus}
                        auditIssues={packagingItem.auditIssues}
                        auditLastRunAt={packagingItem.auditLastRunAt}
                      />
                    </TableCell>
                    {canWriteOrgData && (
                      <TableCell className='text-end'>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          disabled={unlinkPackagingItem.isPending}
                          onClick={() => {
                            void unlinkPackagingItem.mutateAsync({
                              supplierId: packedItem.supplier.id,
                              packagingItemId: packagingItem.id,
                              packedItemId: packedItem.id,
                            })
                          }}
                        >
                          {t('itemDetail.unlinkPackedItem')}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </>
  )
}
